// src/app/services/performance-series/selectors.ts
//
// The bridge from the normalized contract to the EXISTING shared chart shell
// (AnalyticsChart). Given a layer and a chosen dimension, it produces the exact
// ChartDataPoint[] the shell already consumes — same {date,timestamp,overall,
// [memberKey]} shape the legacy per-chart APIs emitted — so the new lenses render
// through the proven renderer with no chart fork.
//
// It preserves two pieces of shipped, accepted behavior so nothing regresses:
//   - measurement-only model lanes (gptweb) are excluded from the model lines,
//     exactly as deep-signal-series-api did;
//   - a member missing at a run timestamp HOLDS at the aggregate for that
//     timestamp, so a line never breaks into a gap.
//
// Imports the central model catalog (canonical keys/labels) and the registry
// (units/labels). Otherwise pure.

import { normalizeProviderKey } from "@/app/constants/models";
import type { SeriesIncompleteRun } from "@/app/services/analytics/deep-signal-series-api";
import type {
  NormalizedEnvelope,
  NormalizedLayer,
  SeriesPoint,
  DimensionName,
} from "./types";
import {
  chartScaleFor,
  chartSuffixFor,
  memberLabel,
  isExcludedModelMember,
} from "./registry";

// Mirror of the shell's ChartDataPoint (kept local so this file stays free of UI
// imports; the shapes are structurally identical).
export interface ChartDataPoint {
  date: string;
  timestamp: number;
  overall: number;
  [key: string]: number | string;
}

export interface LensChartData {
  /** Rows for AnalyticsChart.timeSeriesData. */
  timeSeriesData: ChartDataPoint[];
  /** Data keys to plot == AnalyticsChart.selectedModels. */
  seriesKeys: string[];
  /** display key -> human label (for any UI outside the chart legend). */
  keyLabels: Record<string, string>;
  /** Unit suffix for AnalyticsChart.metricUnit ("%", "", ...). */
  metricUnit: string;
  /** Distinct run timestamps in view (1 => baseline-only). */
  runCount: number;
}

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

function formatDate(t: number): string {
  return new Date(t).toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function toMap(points: SeriesPoint[], scale: number): Map<number, number> {
  const m = new Map<number, number>();
  for (const p of points) m.set(p.t, round2(p.v * scale));
  return m;
}

/**
 * Build the chart series for one layer sliced by one dimension.
 *
 * dimension === "overall" -> a single aggregate line.
 * dimension === "model"   -> one line per (analytics-eligible) model, keyed by
 *                            canonical id so the shell's known colors/labels
 *                            apply; the aggregate drives the header KPI.
 * other dimensions        -> one line per member, keyed by its friendly label so
 *                            the shell legend never shows a raw id.
 */
export function buildLensChartData(
  envelope: NormalizedEnvelope,
  layer: NormalizedLayer,
  dimension: DimensionName,
): LensChartData {
  const scale = chartScaleFor(layer.unit);
  const metricUnit = chartSuffixFor(layer.unit);
  const overallMap = toMap(layer.overall, scale);

  // Resolve the members to plot (empty for the overall lens).
  const keyLabels: Record<string, string> = {};
  const memberMaps: Array<{ displayKey: string; map: Map<number, number> }> = [];

  if (dimension !== "overall") {
    const members = layer.members[dimension] ?? {};
    const usedKeys = new Set<string>();
    for (const [rawKey, points] of Object.entries(members)) {
      if (isExcludedModelMember(dimension, rawKey)) continue; // drop gptweb etc.
      const displayKey = dimension === "model" ? normalizeProviderKey(rawKey) : memberLabel(envelope, dimension, rawKey);
      if (!displayKey || usedKeys.has(displayKey)) continue; // first wins on collision
      usedKeys.add(displayKey);
      keyLabels[displayKey] = memberLabel(envelope, dimension, rawKey);
      memberMaps.push({ displayKey, map: toMap(points, scale) });
    }
  }

  // Row timestamps: the union of the aggregate and every plotted member.
  const tsSet = new Set<number>();
  if (dimension === "overall") {
    for (const t of overallMap.keys()) tsSet.add(t);
  } else {
    for (const t of overallMap.keys()) tsSet.add(t);
    for (const { map } of memberMaps) for (const t of map.keys()) tsSet.add(t);
  }
  const timestamps = [...tsSet].sort((a, b) => a - b);

  const seriesKeys = dimension === "overall" ? ["overall"] : memberMaps.map((m) => m.displayKey);

  const timeSeriesData: ChartDataPoint[] = timestamps.map((t) => {
    // Aggregate at t: the real overall if present, else the mean of the members
    // that reported at t (an axis/header aid for layers with no aggregate line).
    let overall: number;
    if (overallMap.has(t)) {
      overall = overallMap.get(t)!;
    } else if (memberMaps.length) {
      const vals: number[] = [];
      for (const { map } of memberMaps) {
        const v = map.get(t);
        if (v != null) vals.push(v);
      }
      overall = vals.length ? round2(vals.reduce((s, x) => s + x, 0) / vals.length) : 0;
    } else {
      overall = 0;
    }

    const row: ChartDataPoint = { timestamp: t, date: formatDate(t), overall };
    for (const { displayKey, map } of memberMaps) {
      // Hold at the aggregate where a member did not report (no broken lines).
      row[displayKey] = map.has(t) ? map.get(t)! : overall;
    }
    return row;
  });

  return { timeSeriesData, seriesKeys, keyLabels, metricUnit, runCount: timestamps.length };
}

/**
 * The incomplete runs among a layer's points, labelled with the same date
 * string the chart rows carry, so AnalyticsChart can list them under the chart.
 * Empty when the envelope flags no runs.
 */
export function incompleteRunMarks(
  envelope: NormalizedEnvelope,
  layer: NormalizedLayer,
): SeriesIncompleteRun[] {
  const flagged = envelope.incompleteRuns;
  if (!flagged) return [];
  const byRun = new Map<string, number>();
  const visit = (points: SeriesPoint[]) => {
    for (const p of points) if (p.run && flagged[p.run] && !byRun.has(p.run)) byRun.set(p.run, p.t);
  };
  visit(layer.overall);
  for (const members of Object.values(layer.members)) for (const points of Object.values(members)) visit(points);
  return [...byRun.entries()]
    .sort((a, b) => a[1] - b[1])
    .map(([run, t]) => ({ date: formatDate(t), timestamp: t, completeness: flagged[run] }));
}

/**
 * Build a single-line chart series for ONE member of a dimension (e.g. one
 * research variable), for the drill-in trend chart. The header/aggregate is that
 * member's own value, so the chart reads as "this attribute over time".
 */
export function buildMemberChartData(
  envelope: NormalizedEnvelope,
  layer: NormalizedLayer,
  dimension: DimensionName,
  memberKey: string,
): LensChartData {
  const scale = chartScaleFor(layer.unit);
  const metricUnit = chartSuffixFor(layer.unit);
  const label = memberLabel(envelope, dimension, memberKey);
  const points = (layer.members[dimension] ?? {})[memberKey] ?? [];
  const map = toMap(points, scale);
  const timestamps = [...map.keys()].sort((a, b) => a - b);
  const timeSeriesData: ChartDataPoint[] = timestamps.map((t) => {
    const v = map.get(t)!;
    return { timestamp: t, date: formatDate(t), overall: v, [label]: v };
  });
  return { timeSeriesData, seriesKeys: [label], keyLabels: { [label]: label }, metricUnit, runCount: timestamps.length };
}

// ── Variable ranked-list helpers ─────────────────────────────────────────────

export interface VariableRow {
  key: string;          // rv_id
  label: string;        // display label
  score: number;        // latest value, display units (e.g. 0-100)
  baseline: number;     // first value, display units
  /**
   * score - baseline, display units, rounded. NULL when the member has fewer
   * than two readings.
   *
   * A single reading is its own baseline, so differencing it against itself
   * yields 0 — and a rendered "0.0" reads as "measured, no movement" when the
   * truth is "measured once, nothing to compare yet". Sunbit is exactly this
   * case: one scored run, so every variable and every funnel metric has a
   * single point. Callers must render null as "first reading", never as zero.
   */
  delta: number | null;
  history: number[];    // display-unit values oldest->newest (for the sparkline)
  points: number;       // number of readings
}

/** Per-variable summary rows for the ranked list, sorted by score desc. */
export function buildVariableRows(
  envelope: NormalizedEnvelope,
  layer: NormalizedLayer,
  dimension: DimensionName,
): VariableRow[] {
  const scale = chartScaleFor(layer.unit);
  const members = layer.members[dimension] ?? {};
  const rows: VariableRow[] = Object.entries(members).map(([key, pts]) => {
    const history = pts.map((p) => round2(p.v * scale));
    const score = history.length ? history[history.length - 1] : 0;
    const baseline = history.length ? history[0] : 0;
    return {
      key,
      label: memberLabel(envelope, dimension, key),
      score,
      baseline,
      delta: history.length >= 2 ? round2(score - baseline) : null,
      history,
      points: history.length,
    };
  });
  return rows.sort((a, b) => b.score - a.score);
}

// ── KPI helpers (headline/funnel cards) ──────────────────────────────────────

/** Last aggregate point in native units, or null if the layer has no aggregate. */
export function latestOverall(layer: NormalizedLayer): SeriesPoint | null {
  return layer.overall.length ? layer.overall[layer.overall.length - 1] : null;
}

/** Second-to-last aggregate point in native units (for trend deltas), or null. */
export function previousOverall(layer: NormalizedLayer): SeriesPoint | null {
  return layer.overall.length >= 2 ? layer.overall[layer.overall.length - 2] : null;
}
